import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown, Package, User, LogOut, Layers, UserPlus, Globe, Bot, BookOpen, Crown } from 'lucide-react'
import { ChainLogo } from '@/components/ui/ChainLogo'
import { Button } from '@/components/ui/Button'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { useWallet } from '@/hooks/useWallet'
import { useWalletStore } from '@/stores/walletStore'
import { useAuthStore } from '@/stores/authStore'
import { getChainConfig, type ChainConfig } from '@/lib/wallet/chains'
import { walletService } from '@/lib/wallet/walletService'
import { toast } from '@/stores/toastStore'

// ─── Nav links ────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { to: '/catalog',          label: 'Catalog' },
  { to: '/open-marketplace', label: 'Marketplace' },
  { to: '/contracts',        label: 'Contracts' },
  { to: '/pricing',          label: 'Pricing' },
  { to: '/sell',             label: 'Sell' },
]

// ─── Supported networks for the platform ─────────────────────────────────────

const PLATFORM_NETWORKS: { chainId: number; config: ChainConfig }[] = [
  { chainId: 50,    config: getChainConfig(50)! },    // XDC Mainnet (default)
  { chainId: 84532, config: getChainConfig(84532)! }, // Base Sepolia
  { chainId: 43113, config: getChainConfig(43113)! }, // Avalanche Fuji
].filter((n) => n.config)

// ─── Network selector dropdown ───────────────────────────────────────────────

function NetworkSelector() {
  const chainId = useWalletStore((s) => s.chainId)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const currentConfig = chainId ? getChainConfig(chainId) : null
  const isSupported = PLATFORM_NETWORKS.some((n) => n.chainId === chainId)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  async function handleSwitch(targetChainId: number) {
    setOpen(false)
    if (Number(chainId) === targetChainId) return
    try {
      await walletService.switchChain(targetChainId)
    } catch (err: unknown) {
      const label = getChainConfig(targetChainId)?.name ?? `chain ${targetChainId}`
      toast.error(`Switch to ${label} failed`, err instanceof Error ? err.message : 'Rejected')
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={[
          'flex items-center gap-1.5 h-8 px-2.5 rounded-lg border transition-colors',
          isSupported
            ? 'bg-[#141414] border-[#1E1E1E] hover:border-[#E8850C]'
            : 'bg-[rgba(239,68,68,0.08)] border-[rgba(239,68,68,0.2)] hover:border-[rgba(239,68,68,0.4)]',
        ].join(' ')}
        title="Switch network"
      >
        {currentConfig ? (
          <>
            <ChainLogo chainId={Number(chainId)} size={16} />
            <span
              className="text-[10px] font-semibold"
              style={{ color: currentConfig.color }}
            >
              {currentConfig.shortName}
            </span>
          </>
        ) : (
          <>
            <Globe size={12} className="text-[#EF4444]" />
            <span className="text-[10px] font-semibold text-[#EF4444]">Wrong</span>
          </>
        )}
        <ChevronDown size={10} className={`text-[#6B6B6B] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full mt-1.5 w-52 bg-[#141414] border border-[#1E1E1E] rounded-xl p-1.5 shadow-[0_8px_40px_rgba(0,0,0,0.6)] z-50"
          >
            <p className="text-[9px] text-[#555] font-semibold uppercase tracking-widest px-2.5 py-1.5">
              Select Network
            </p>
            {PLATFORM_NETWORKS.map(({ chainId: cid, config }) => {
              const isActive = Number(chainId) === cid
              return (
                <button
                  key={cid}
                  onClick={() => void handleSwitch(cid)}
                  className={[
                    'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-xs transition-colors',
                    isActive
                      ? 'bg-[rgba(232,133,12,0.08)] text-[#E8850C]'
                      : 'text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#1A1A1A]',
                  ].join(' ')}
                >
                  <ChainLogo chainId={cid} size={18} />
                  <div className="flex-1 text-left">
                    <p className="font-medium">{config.name}</p>
                    {config.isTestnet && (
                      <span className="text-[8px] text-[#555] uppercase tracking-wider">Testnet</span>
                    )}
                  </div>
                  {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  )}
                </button>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Connected dropdown ───────────────────────────────────────────────────────

interface ConnectedMenuProps {
  shortAddress: string
  isAdmin: boolean
  onDisconnect: () => void
}

function ConnectedMenu({ shortAddress, isAdmin, onDisconnect }: ConnectedMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const itemCls =
    'flex items-center gap-2.5 w-full px-3 py-2 text-xs text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#1A1A1A] transition-colors rounded-lg'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-8 px-3 rounded-lg bg-[#141414] border border-[#1E1E1E] hover:border-[#E8850C] transition-colors"
      >
        <span className="w-1.5 h-1.5 bg-[#22C55E] rounded-full shrink-0" />
        <span className="text-xs text-[#A0A0A0] font-mono">{shortAddress}</span>
        <ChevronDown
          size={12}
          className={`text-[#6B6B6B] transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full mt-1.5 w-48 bg-[#141414] border border-[#1E1E1E] rounded-xl p-1.5 shadow-[0_8px_40px_rgba(0,0,0,0.6)] z-50"
          >
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className={itemCls}
            >
              <Package size={13} />
              My Products
            </Link>
            <Link
              to="/profile?tab=general"
              onClick={() => setOpen(false)}
              className={itemCls}
            >
              <User size={13} />
              Profile
            </Link>
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              className={itemCls}
            >
              <Bot size={13} />
              Agent Dashboard
            </Link>
            <Link
              to="/vip"
              onClick={() => setOpen(false)}
              className={itemCls}
            >
              <Crown size={13} />
              VIP API Docs
            </Link>
            <Link
              to="/learn"
              onClick={() => setOpen(false)}
              className={itemCls}
            >
              <BookOpen size={13} />
              Docs
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className={itemCls}
              >
                <Layers size={13} />
                Studio
              </Link>
            )}
            <div className="my-1 border-t border-[#1E1E1E]" />
            <button
              onClick={() => { onDisconnect(); setOpen(false) }}
              className={`${itemCls} text-[#EF4444] hover:text-[#EF4444] hover:bg-[rgba(239,68,68,0.08)]`}
            >
              <LogOut size={13} />
              Disconnect
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

export function Navbar() {
  const location = useLocation()
  const { isConnected, isAdmin, shortAddress, connect, disconnect } = useWallet()
  const { isAuthenticated: _isAuthenticated } = useAuthStore()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMobileOpen(false), [location.pathname])

  const linkCls = (to: string) => {
    const active = location.pathname === to || location.pathname.startsWith(to + '/')
    return [
      'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150',
      active
        ? 'text-[#E8850C] bg-[rgba(232,133,12,0.08)]'
        : 'text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#141414]',
    ].join(' ')
  }

  const mobileLinkCls = (to: string) => {
    const active = location.pathname === to || location.pathname.startsWith(to + '/')
    return [
      'flex items-center px-3 py-2.5 rounded-lg text-sm transition-colors',
      active ? 'text-[#E8850C] bg-[rgba(232,133,12,0.08)]' : 'text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#141414]',
    ].join(' ')
  }

  return (
    <header
      className={[
        'fixed top-0 inset-x-0 z-40 transition-[background-color,backdrop-filter] duration-300 border-b',
        scrolled
          ? 'nav-scrolled backdrop-blur-xl border-[#181818]'
          : 'bg-transparent backdrop-blur-sm border-transparent',
      ].join(' ')}
    >
      {/*
        Three-column grid: [1fr | auto | 1fr]
        ─────────────────────────────────────
        - The middle column is auto-sized to the nav-link content.
        - The two `1fr` outer columns balance against each other, so the
          middle column ends up *geometrically* centred against the nav
          element, regardless of how wide the logo or wallet CTA become.
        - A flex layout with `justify-between` cannot do this: the centre
          gets pushed off-axis whenever the left and right widths differ.
      */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-6">

        {/*
          Each child explicitly anchored to its column with `col-start-*`.
          Without this, when the centre column is `display:none` on mobile
          the grid auto-flow would slide the right wrapper into column 2,
          breaking alignment. Explicit placement keeps every child in its
          intended column at all breakpoints.
        */}

        {/* ── Left: S6 Labs logo + SaaSFarm ── */}
        <Link to="/" className="col-start-1 flex items-center gap-2.5 shrink-0 group justify-self-start">
          <div className="w-7 h-7 rounded-lg bg-[#E8850C] flex items-center justify-center shadow-[0_0_16px_rgba(232,133,12,0.45)] group-hover:shadow-[0_0_24px_rgba(232,133,12,0.6)] transition-shadow duration-300">
            <span className="text-white text-[11px] font-bold tracking-tight">S6</span>
          </div>
          <span className="text-[#E8E8E8] font-semibold text-sm tracking-tight hidden xs:block sm:block">
            SaaSFarm
          </span>
        </Link>

        {/* ── Center: primary nav ── */}
        <div className="col-start-2 hidden md:flex items-center gap-0.5 justify-self-center">
          {NAV_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} className={linkCls(to)}>
              {label}
            </Link>
          ))}
        </div>

        {/* ── Right: theme toggle + wallet CTA ── */}
        <div className="col-start-3 flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0 justify-self-end">
          {/* Theme toggle hidden on the smallest screens to avoid right-side overflow.
              It is still accessible via the mobile drawer footer. */}
          <div className="hidden sm:block">
            <ThemeToggle />
          </div>

          {isConnected && shortAddress ? (
            <div className="flex items-center gap-1.5 min-w-0">
              {/* Network selector hidden on phones to keep the navbar single-line.
                  Users can switch network from the mobile drawer. */}
              <div className="hidden sm:block">
                <NetworkSelector />
              </div>
              <ConnectedMenu
                shortAddress={shortAddress}
                isAdmin={isAdmin}
                onDisconnect={disconnect}
              />
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/register"
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#141414] transition-colors"
              >
                Register
              </Link>
              <Button size="sm" onClick={connect}>
                Connect Wallet
              </Button>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#141414] transition-colors shrink-0"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      {/* ── Mobile slide-out drawer ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden bg-[#0A0A0A]/98 backdrop-blur-md border-b border-[#1E1E1E]"
          >
            <div className="px-4 py-3 flex flex-col gap-1">
              {NAV_LINKS.map(({ to, label }) => (
                <Link key={to} to={to} className={mobileLinkCls(to)}>
                  {label}
                </Link>
              ))}

              <div className="border-t border-[#1E1E1E] my-2" />

              {isConnected && shortAddress ? (
                <>
                  <Link to="/profile" className={mobileLinkCls('/profile')}>
                    My Products
                  </Link>
                  <Link to="/profile?tab=general" className={mobileLinkCls('/profile?tab=general')}>
                    Profile
                  </Link>
                  <Link to="/dashboard" className={mobileLinkCls('/dashboard')}>
                    Agent Dashboard
                  </Link>
                  <Link to="/vip" className={mobileLinkCls('/vip')}>
                    VIP API Docs
                  </Link>
                  <Link to="/learn" className={mobileLinkCls('/learn')}>
                    Docs
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className={mobileLinkCls('/admin')}>
                      Studio
                    </Link>
                  )}

                  {/* Network selector — only shown in the drawer on mobile */}
                  <div className="px-3 py-2 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-[#6B6B6B] font-semibold uppercase tracking-widest">
                      Network
                    </span>
                    <NetworkSelector />
                  </div>

                  <button
                    onClick={disconnect}
                    className="flex items-center px-3 py-2.5 rounded-lg text-sm text-[#EF4444] hover:bg-[rgba(239,68,68,0.08)] transition-colors"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 mt-1">
                  <Link to="/register" className={mobileLinkCls('/register')}>
                    <UserPlus size={14} className="mr-2" />
                    Register
                  </Link>
                  <Button size="sm" onClick={connect}>
                    Connect Wallet
                  </Button>
                </div>
              )}

              {/* Theme toggle — shown in the drawer on mobile, since the navbar
                  hides it on small screens to save horizontal space. */}
              <div className="border-t border-[#1E1E1E] my-2" />
              <div className="px-3 py-2 flex items-center justify-between gap-2">
                <span className="text-[10px] text-[#6B6B6B] font-semibold uppercase tracking-widest">
                  Theme
                </span>
                <ThemeToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
